import { supabase } from '@/integrations/supabase/client';
import type {
  InfluencerFilters,
  InfluencerRow,
  SearchInfluencerRow,
} from '../types/influencer.types';

export const influencerRepository = {
  /** Search and paginate influencers for the current organization */
  async search(filters: InfluencerFilters = {}) {
    const {
      searchTerm = '',
      gender,
      platform,
      engagement,
      location,
      tier,
      status,
      page = 1,
      pageSize = 20,
    } = filters;

    const { data, error } = await supabase.rpc('search_influencers', {
      p_search_term: searchTerm?.trim() || null,
      p_gender: gender || null,
      p_platform: platform || 'all',
      p_engagement: engagement || 'all',
      p_location: location || 'all',
      p_tier: tier || 'all',
      p_status: status || 'all',
      p_page: page,
      p_page_size: pageSize,
    });

    if (error) throw error;
    const rows = (data as unknown as SearchInfluencerRow[]) || [];
    const totalCount = rows.length > 0 ? rows[0].total_count : 0;

    return {
      rows,
      totalCount,
      totalPages: Math.ceil(totalCount / pageSize),
      currentPage: page,
      pageSize,
    };
  },

  /** Fetch a single influencer by ID with verified organization authorization */
  async getById(id: string, orgId: string) {
    if (!id) throw new Error('Influencer ID is required');
    if (!orgId) throw new Error('Organization ID is required');

    const { data, error } = await supabase.rpc('get_influencer_by_id', {
      p_org_id: orgId,
      p_id: id,
    });

    if (error) throw error;
    const row = data?.[0];
    if (!row) throw new Error('Influencer not found');
    return row;
  },

  /** Fetch minimal formatted options for cross-domain usage */
  async getOptions(orgId?: string) {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    const { data, error } = await supabase.rpc('get_all_influencers_formatted', {
      p_org_id: orgId || null,
    });
    if (error) throw error;

    return ((data as unknown as Array<Record<string, unknown>>) || []).map((row) => {
      const rawThumbnails = (row.thumbnails as string[]) || [];
      const thumbnailPic = Array.isArray(rawThumbnails) && typeof rawThumbnails[0] === 'string'
        ? rawThumbnails[0]
        : null;
      return {
        id: row.id as string,
        name: (row.name as string) || (row.handle as string),
        handle: row.handle as string,
        platform: row.platform as string,
        profile_pic_url: thumbnailPic || null,
        followers_count: (row.followers_count as number) || 0,
        tier: row.tier as string | null,
      };
    });
  },

  /** Delete a batch of influencers by IDs */
  async deleteMany(ids: string[]): Promise<void> {
    if (!ids || ids.length === 0) return;
    const { error } = await supabase.from('influencers').delete().in('id', ids);
    if (error) throw error;
  },

  /** Update an influencer profile */
  async update(id: string, updates: Partial<InfluencerRow>): Promise<InfluencerRow> {
    const { data, error } = await supabase
      .from('influencers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as InfluencerRow;
  },
};
